import { useMemo, useState, type FC } from 'react';
import type { HeroDetailedProps } from './types';

import styled, { css } from 'styled-components';
import {
  COLOR_ORANGE,
  COLOR_YELLOW,
  defaultWebValue,
  filterBackgroundColor,
  filterBackgroundColorMultiple,
  sanitizeRichText
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Button, Wrapper, Mockup, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Image as ImageComp } from 'shared/components/Common';
import { HoverableDropzone } from '../HoverableDropzone';

import SvgIcons from 'shared/components/Common/IconsHeroDetailed';
import useGeneralActions from '../hooks/useGeneralActions';

const HeroDetailed = styled.div`
  position: relative;
  overflow-x: hidden;
  min-height: 693px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
  flex-direction: column;

  `}
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  display: flex;
  justify-content: space-between;
  padding: 70px 0 70px 0px;
  max-width: 100%;
  gap: 50px;

  @media screen and (max-width: 710px) {
    gap: 40px;
    flex-direction: column-reverse;
  }
`;

const TitleWrapper = styled.div`
  width: 655px;
  position: relative;
  z-index: 7;
  margin-top: -19px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 472px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 0 15px;
    `}
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  line-height: 1.25em;
  color: #fff;
  margin-bottom: 34px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin-bottom: 80px;
  line-height: 1.35em;
  color: #fff;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 59px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 40px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const ButtonInner = styled.div`
  position: relative;
  width: fit-content;
`;

const StyledButton = styled(Button)`
  max-width: 385px;
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    max-width: 360px;
    width: 100%;
    padding-top: 24px;
    padding-bottom: 26px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    max-width: 300px;

  `}
`;

const SvgArrow = styled.svg`
  position: absolute;
  right: -156px;
  width: 130px;
  top: -40px;
  opacity: 0.4;

  @media screen and (max-width: 900px) {
    display: none;
  }
`;

const RelativeWrapper = styled.div`
  position: relative;
  z-index: 6;
  width: 704px;
  height: 704px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: -4%;

  @media screen and (max-width: 991px) {
    width: 584px;
    height: 584px;
  }

  @media screen and (max-width: 710px) {
    left: 0;
    right: 0;
    margin: 0 auto;
  }

  @media screen and (max-width: 480px) {
    width: 100%;
    margin: 0 auto;
  }
`;

const SvgBg = styled.svg`
  width: 109%;
  height: 109%;
  position: absolute;
  top: -4.5%;
  left: -4.5%;
  right: 0;
  margin: 0 auto;
  z-index: 1;

  .cls-1 {
    fill: none;
    stroke: #fff;
    stroke-width: 3px;
  }

  .cls-3 {
    fill: #fff;
  }

  @media screen and (max-width: 710px) {
    top: 0px;
    left: 0px;
    right: 0;
    width: 100%;
    height: 100%;
  }
`;

const StyledMockup = styled((props) => <Mockup {...props} />)`
  width: 506px;
  height: auto;
  object-fit: fill;
  z-index: 2;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 438px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 340px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 370 &&
    `
    width: 270px;
  `}
`;

const BenefitWrapper = styled.div`
  position: relative;
`;

const Benefits = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  z-index: 24;
  border-top: 1px solid rgb(255 255 255 / 31%);
  padding: 50px 0 90px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
    bottom: auto;
    padding-bottom: 80px;
    padding-top: 50px;
    `}
`;

const BenefitItem = styled.div`
  width: 31.5%;
  padding: 24px;
  border-radius: 17px;
  background: #fff;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.08);
  display: flex;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    flex-direction: column;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 32.5%;
    padding: 18px 18px 20px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: row;
    width: 100%;
    padding: 18px 15px;
    border-radius: 12px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
    `}
`;

const StyledSvgIcons = styled(SvgIcons)`
  width: 62px;
  height: 62px;
  margin-right: 18px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 52px;
      height: 52px;
      margin-bottom: 8px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 52px;
    height: 52px;
    margin-bottom: 8px;
  `}
`;

const BenefitTxtInner = styled.div`
  width: 78%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
    `}
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 12px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const WhiteInner = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 3;
  overflow: hidden;
`;

const RelativeWhite = styled.div`
  position: relative;
  width: 1920px;
  height: 100%;
  margin: 0 auto;

  .hero-white-v5 {
    border-radius: 50%;
    filter: blur(128px);
    background: #fff;
    position: absolute;
    margin: 0 auto;
    left: 0;
    right: 0;
  }

  .hero-white-v5_one {
    width: 810px;
    height: 810px;
    opacity: 0.3;
    bottom: -20%;
    right: -44%;
  }

  .hero-white-v5_two {
    width: 522px;
    height: 522px;
    opacity: 0.3;
    background: #fff;
    right: -38%;
    bottom: 25%;
  }

  .hero-white-v5_three {
    width: 609px;
    height: 609px;
    opacity: 0.5;
    background: #fff;
    right: -28%;
    bottom: 30%;
  }

  .hero-white-v5_four {
    width: 1307px;
    height: 1307px;
    opacity: 0.33;
    background: #fff;
    left: -52%;
    bottom: -74%;
  }

  .hero-white-v5_five {
    width: 322px;
    height: 322px;
    opacity: 0.3;
    background: #fff;
    left: -68%;
    top: 1%;
  }

  .hero-white-v5_six {
    width: 322px;
    height: 322px;
    opacity: 0.24;
    background: #fff;
    right: -78%;
    top: 1%;
  }

  @media screen and (max-width: 575px) {
    .hero-white-v5_three {
      width: 609px;
      height: 609px;
      opacity: 0.35;
      background: #fff;
      right: 22%;
      bottom: auto;
      top: -3%;
    }

    .hero-white-v5_six {
      width: 322px;
      height: 322px;
      opacity: 0.54;
      background: #fff;
      left: 0;
      right: auto;
      top: 21%;
    }

    .hero-white-v5_two {
      width: 382px;
      height: 382px;
      opacity: 0.5;
      background: #fff;
      right: auto;
      bottom: 15%;
    }
  }
`;

const ColoredBg = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  border-top: 1px solid rgb(255 255 255 / 16%);

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    height: 100%;
    `}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  height: 685px;
  width: 513px;

  @media screen and (max-width: 991px) {
    width: 440px;
    height: 580px;
  }
`;

const BookMediaImg = styled(ImageComp)<{ isImageUploaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  border-radius: ${(props) => (props.isImageUploaded ? '5px' : '0px')};
`;

export const V5: FC<HeroDetailedProps> = (props) => {
  const {
    mockup,
    cover_thumbnail,
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    secondaryColor = COLOR_YELLOW,
    backgroundColor = COLOR_ORANGE,
    textColor,
    benefits = [],
    meta,
    hidden
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { isCoverDisabled, pages, bookMedia } = useBook();
  const { pagesThumbnails, updateImageMeta, purchaseElementId } = useSales();
  const { handleUploadAction } = useGeneralActions(props);
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const [isLoading, setIsLoading] = useState(false);
  const [isImageUploaded, setIsImageUploaded] = useState(false);

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, pagesThumbnails, cover_thumbnail]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      setIsImageUploaded(true);
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const handleUpload = async (file: File) => {
    setIsLoading(true);
    await handleUploadAction(file, setResize, 'book_mockup');
    setIsLoading(false);
  };

  return (
    <HeroDetailed
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}>
      <StyledWrapper>
        <TitleWrapper>
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, process.env['APP_TYPE'] === 'dashboard')
            }}
            style={{
              color: filterBackgroundColorMultiple([backgroundColor, secondaryColor], textColor)
            }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="title"
            data-placeholder="+"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isTitleEmpty}
          />
          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{
              color: filterBackgroundColorMultiple([backgroundColor, secondaryColor], textColor)
            }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="content"
            data-placeholder="+"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isContentEmpty}
          />
          {meta['url'] ? (
            <Download textColor={textColor} button={button} meta={meta} />
          ) : (
            <ButtonInner>
              <StyledButton to={purchaseElementId} button={button} meta={meta} />
              <SvgArrow
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 159 95"
                fill={filterBackgroundColorMultiple([backgroundColor, secondaryColor], textColor)}>
                <path d="M156.727 7.9416C157.049 7.493 157.674 7.3905 158.122 7.71265C158.571 8.03479 158.673 8.65961 158.351 9.1082L156.727 7.9416ZM79.0294 52.8477L79.3711 51.9079L79.0294 52.8477ZM79.1792 2.52399L79.4639 3.48261L79.1792 2.52399ZM0.92325 90.0239L10.2786 83.2556L11.4625 94.7418L0.92325 90.0239ZM2.86976 88.818C3.41876 88.7579 3.91256 89.1542 3.97271 89.7032C4.03285 90.2522 3.63655 90.746 3.08755 90.8061L2.86976 88.818ZM7.15782 90.3074C6.61054 90.3817 6.10671 89.9982 6.03246 89.4509C5.95822 88.9036 6.34169 88.3998 6.88896 88.3256L7.15782 90.3074ZM10.867 87.7329C11.4121 87.6444 11.9259 88.0145 12.0144 88.5596C12.103 89.1048 11.7329 89.6185 11.1877 89.707L10.867 87.7329ZM15.24 88.9924C14.6975 89.0957 14.1739 88.7396 14.0706 88.1971C13.9673 87.6546 14.3233 87.131 14.8658 87.0277L15.24 88.9924ZM18.7962 86.2233C19.3357 86.1051 19.8688 86.4466 19.987 86.9861C20.1052 87.5256 19.7637 88.0587 19.2242 88.1769L18.7962 86.2233ZM23.2384 87.2375C22.7025 87.371 22.1598 87.0448 22.0263 86.509C21.8927 85.9731 22.2189 85.4304 22.7548 85.2968L23.2384 87.2375ZM26.6308 84.2717C27.1626 84.1228 27.7145 84.4333 27.8634 84.9651C28.0123 85.4969 27.7019 86.0488 27.17 86.1977L26.6308 84.2717ZM31.1247 85.0267C30.5975 85.1914 30.0367 84.8976 29.8719 84.3704C29.7072 83.8433 30.001 83.2824 30.5282 83.1177L31.1247 85.0267ZM34.3397 81.8635C34.8616 81.683 35.4311 81.9597 35.6116 82.4817C35.7922 83.0036 35.5154 83.5731 34.9935 83.7536L34.3397 81.8635ZM38.8659 82.3465C38.3498 82.5432 37.772 82.2843 37.5753 81.7682C37.3786 81.2522 37.6374 80.6743 38.1535 80.4776L38.8659 82.3465ZM41.8873 78.9868C42.3969 78.7739 42.9826 79.0145 43.1954 79.5241C43.4083 80.0337 43.1678 80.6194 42.6582 80.8323L41.8873 78.9868ZM46.4246 79.187C45.9221 79.4163 45.329 79.1948 45.0997 78.6924C44.8704 78.1899 45.0919 77.5968 45.5943 77.3675L46.4246 79.187ZM49.2341 75.6338C49.7287 75.3881 50.3289 75.5899 50.5746 76.0845C50.8203 76.5792 50.6185 77.1793 50.1239 77.425L49.2341 75.6338ZM53.7594 75.5426C53.2733 75.8048 52.6667 75.6232 52.4046 75.1371C52.1424 74.651 52.3239 74.0445 52.81 73.7823L53.7594 75.5426ZM56.337 71.8012C56.8138 71.5224 57.4263 71.6829 57.705 72.1597C57.9838 72.6365 57.8232 73.249 57.3465 73.5277L56.337 71.8012ZM60.8262 71.4115C60.3594 71.7066 59.7417 71.5675 59.4465 71.1007C59.1514 70.6339 59.2905 70.0162 59.7573 69.7211L60.8262 71.4115ZM63.1487 67.4903C63.6045 67.1785 64.2269 67.2953 64.5387 67.7512C64.8504 68.207 64.7337 68.8293 64.2778 69.1411L63.1487 67.4903ZM67.6168 66.7669C67.1727 67.0951 66.5465 67.0012 66.2183 66.557C65.89 66.1129 65.984 65.4867 66.4281 65.1585L67.6168 66.7669ZM69.6172 62.7085C70.0488 62.3638 70.678 62.4343 71.0227 62.8659C71.3673 63.2974 71.2968 63.9267 70.8652 64.2713L69.6172 62.7085ZM74.0365 61.6375C73.6186 61.9985 72.9871 61.9524 72.6261 61.5344C72.265 61.1165 72.3112 60.485 72.7291 60.124L74.0365 61.6375ZM75.7168 57.4388C76.1202 57.0616 76.753 57.0828 77.1302 57.4861C77.5075 57.8895 77.4863 58.5223 77.0829 58.8996L75.7168 57.4388ZM80.0215 56.0374C79.6339 56.4308 79.0007 56.4354 78.6073 56.0478C78.214 55.6601 78.2093 55.027 78.597 54.6336L80.0215 56.0374ZM81.3722 51.6957C81.7428 51.2862 82.3751 51.2546 82.7846 51.6252C83.1941 51.9958 83.2257 52.6281 82.8551 53.0376L81.3722 51.6957ZM85.5447 49.9313C85.1926 50.3567 84.5622 50.4162 84.1367 50.0641C83.7113 49.7119 83.6518 49.0816 84.0039 48.6561L85.5447 49.9313ZM86.4846 45.5151C86.8167 45.0738 87.4436 44.9853 87.8849 45.3174C88.3262 45.6495 88.4147 46.2765 88.0826 46.7177L86.4846 45.5151ZM90.4819 43.3624C90.172 43.8195 89.5502 43.9388 89.093 43.6288C88.6359 43.3189 88.5166 42.697 88.8266 42.2399L90.4819 43.3624ZM90.9989 38.849C91.2842 38.3761 91.8989 38.2241 92.3717 38.5095C92.8446 38.7948 92.9966 39.4094 92.7113 39.8823L90.9989 38.849ZM94.7436 36.2881C94.4859 36.7766 93.881 36.9637 93.3926 36.706C92.9041 36.4483 92.717 35.8434 92.9747 35.3549L94.7436 36.2881ZM94.7314 31.7577C94.9575 31.2538 95.5493 31.0287 96.0532 31.2548C96.557 31.4809 96.7822 32.0727 96.556 32.5766L94.7314 31.7577ZM98.1163 28.7441C97.927 29.2629 97.353 29.5301 96.8342 29.3409C96.3153 29.1517 96.0481 28.5777 96.2373 28.0588L98.1163 28.7441ZM97.4478 24.2639C97.5926 23.7309 98.1421 23.4163 98.6751 23.5611C99.208 23.706 99.5226 24.2554 99.3778 24.7884L97.4478 24.2639ZM100.271 20.7136C100.182 21.2585 99.6675 21.6278 99.1226 21.5382C98.5776 21.4487 98.2084 20.9343 98.2979 20.3893L100.271 20.7136ZM98.6929 16.4756C98.7107 15.9236 99.1727 15.4906 99.7247 15.5084C100.277 15.5263 100.71 15.9882 100.692 16.5402L98.6929 16.4756ZM100.479 12.3282C100.557 12.8749 100.177 13.3817 99.6307 13.4601C99.084 13.5385 98.5773 13.1589 98.4989 12.6122L100.479 12.3282ZM97.5203 8.91596C97.31 8.40528 97.5535 7.82082 98.0642 7.61053C98.5749 7.40024 99.1594 7.64375 99.3697 8.15444L97.5203 8.91596ZM97.0737 4.43293C97.4412 4.84517 97.405 5.4773 96.9927 5.84482C96.5805 6.21234 95.9483 6.17608 95.5808 5.76383L97.0737 4.43293ZM92.6739 3.54586C92.1798 3.29903 91.9794 2.69842 92.2262 2.20436C92.4731 1.71031 93.0737 1.50989 93.5677 1.75673L92.6739 3.54586ZM89.3824 0.477152C89.9289 0.557066 90.3071 1.06485 90.2272 1.61132C90.1473 2.1578 89.6395 2.53602 89.093 2.4561L89.3824 0.477152ZM85.2877 2.35179C84.737 2.39346 84.2568 1.9808 84.2151 1.43009C84.1735 0.879381 84.5861 0.399162 85.1368 0.357491L85.2877 2.35179ZM80.982 1.01183C81.52 0.886945 82.0573 1.22183 82.1822 1.75981C82.3071 2.29779 81.9722 2.83515 81.4342 2.96003L80.982 1.01183ZM77.9526 4.28873C77.4955 4.5986 76.8737 4.47918 76.5638 4.02202C76.2539 3.56485 76.3734 2.94304 76.8305 2.63318L77.9526 4.28873ZM73.5547 5.35066C73.9537 4.96879 74.5867 4.98267 74.9685 5.38166C75.3504 5.78065 75.3365 6.41366 74.9375 6.79553L73.5547 5.35066ZM72.1843 9.70088C71.8225 10.1182 71.1909 10.1632 70.7736 9.80139C70.3563 9.43961 70.3113 8.80804 70.6731 8.39075L72.1843 9.70088ZM68.0682 11.6503C68.3957 11.2056 69.0217 11.1107 69.4664 11.4382C69.9111 11.7658 70.006 12.3918 69.6785 12.8364L68.0682 11.6503ZM67.4313 16.1554C67.1405 16.6249 66.5241 16.7697 66.0546 16.4789C65.5851 16.1881 65.4402 15.5717 65.7311 15.1022L67.4313 16.1554ZM63.6883 18.7459C63.9362 18.2523 64.5372 18.0531 65.0307 18.3009C65.5243 18.5487 65.7235 19.1497 65.4757 19.6433L63.6883 18.7459ZM63.8726 23.2893C63.6781 23.8062 63.1015 24.0676 62.5845 23.8732C62.0676 23.6788 61.8062 23.1021 62.0006 22.5852L63.8726 23.2893ZM60.7661 26.618C60.8918 26.0802 61.4297 25.7462 61.9675 25.8719C62.5053 25.9977 62.8393 26.5355 62.7135 27.0733L60.7661 26.618ZM62.1225 30.9504C62.0858 31.5014 61.6093 31.9184 61.0583 31.8818C60.5072 31.8451 60.0902 31.3686 60.1269 30.8176L62.1225 30.9504ZM60.2603 35.0972C60.1861 34.5499 60.5697 34.0461 61.117 33.972C61.6643 33.8979 62.168 34.2815 62.2422 34.8288L60.2603 35.0972ZM63.186 38.5591C63.3827 39.0752 63.1237 39.653 62.6077 39.8497C62.0916 40.0463 61.5138 39.7874 61.3171 39.2713L63.186 38.5591ZM63.306 43.0876C62.9968 42.63 63.1171 42.0084 63.5747 41.6992C64.0323 41.3899 64.6539 41.5102 64.9631 41.9678L63.306 43.0876ZM67.4574 44.9374C67.8521 45.3236 67.8591 45.9567 67.4728 46.3515C67.0866 46.7463 66.4535 46.7532 66.0587 46.367L67.4574 44.9374ZM69.3357 49.0722C68.8837 48.7548 68.7746 48.1311 69.092 47.6791C69.4093 47.2272 70.033 47.1181 70.485 47.4354L69.3357 49.0722ZM73.8644 49.492C74.3525 49.7504 74.5388 50.3555 74.2804 50.8437C74.0221 51.3318 73.4169 51.5181 72.9288 51.2597L73.8644 49.492ZM76.7067 53.0216C76.1958 52.8117 75.9519 52.2274 76.1618 51.7166C76.3718 51.2057 76.956 50.9618 77.4669 51.1717L76.7067 53.0216ZM81.2126 52.514C81.7423 52.6705 82.0448 53.2266 81.8884 53.7563C81.732 54.286 81.1758 54.5886 80.6461 54.4321L81.2126 52.514ZM84.6466 55.3514C84.1018 55.2607 83.7337 54.7455 83.8244 54.2008C83.9151 53.656 84.4302 53.2878 84.975 53.3785L84.6466 55.3514ZM88.8227 53.7862C89.3744 53.8123 89.8004 54.2807 89.7743 54.8324C89.7481 55.384 89.2797 55.81 88.7281 55.7839L88.8227 53.7862ZM92.8242 55.7511C92.2729 55.7852 91.7984 55.366 91.7644 54.8147C91.7303 54.2635 92.1495 53.789 92.7007 53.7549L92.8242 55.7511ZM96.5643 53.3207C97.1096 53.2326 97.623 53.6031 97.7111 54.1484C97.7991 54.6936 97.4286 55.207 96.8834 55.2951L96.5643 53.3207ZM100.872 54.4668C100.336 54.6021 99.7927 54.2777 99.6574 53.7423C99.5221 53.2068 99.8465 52.6631 100.382 52.5277L100.872 54.4668ZM104.137 51.4203C104.66 51.2442 105.227 51.5259 105.403 52.0494C105.579 52.5729 105.298 53.1399 104.774 53.316L104.137 51.4203ZM108.57 51.8925C108.06 52.1033 107.475 51.8604 107.264 51.35C107.053 50.8395 107.296 50.2548 107.807 50.044L108.57 51.8925ZM111.39 48.4354C111.887 48.1947 112.485 48.4026 112.726 48.8997C112.967 49.3968 112.759 49.9949 112.262 50.2355L111.39 48.4354ZM115.852 48.3777C115.368 48.644 114.76 48.4676 114.494 47.9837C114.228 47.4999 114.404 46.8918 114.888 46.6255L115.852 48.3777ZM118.298 44.6421C118.769 44.3536 119.385 44.5016 119.673 44.9726C119.962 45.4436 119.814 46.0592 119.343 46.3476L118.298 44.6421ZM122.733 44.1715C122.275 44.4792 121.654 44.3569 121.346 43.8983C121.038 43.4397 121.16 42.8184 121.619 42.5107L122.733 44.1715ZM124.858 40.2467C125.305 39.922 125.93 40.0209 126.255 40.4677C126.579 40.9144 126.481 41.5398 126.034 41.8645L124.858 40.2467ZM129.247 39.4426C128.812 39.7824 128.184 39.7049 127.844 39.2695C127.504 38.834 127.582 38.2056 128.017 37.8659L129.247 39.4426ZM131.098 35.3818C131.523 35.0285 132.153 35.0863 132.506 35.5108C132.86 35.9353 132.802 36.5659 132.377 36.9191L131.098 35.3818ZM135.429 34.3032C135.015 34.6687 134.383 34.6293 134.017 34.2152C133.652 33.8011 133.691 33.1692 134.105 32.8037L135.429 34.3032ZM137.041 30.1397C137.445 29.7631 138.077 29.7852 138.454 30.1891C138.831 30.593 138.809 31.2258 138.405 31.6024L137.041 30.1397ZM141.306 28.8255C140.912 29.2125 140.279 29.2068 139.892 28.8128C139.505 28.4187 139.51 27.7856 139.904 27.3986L141.306 28.8255ZM142.695 24.5881C143.079 24.1914 143.712 24.1813 144.109 24.5655C144.506 24.9498 144.516 25.5829 144.132 25.9796L142.695 24.5881ZM146.891 23.0597C146.517 23.4657 145.884 23.4912 145.478 23.1168C145.072 22.7423 145.047 22.1097 145.421 21.7037L146.891 23.0597ZM148.076 18.7541C148.441 18.3391 149.073 18.2982 149.487 18.6626C149.902 19.027 149.943 19.6589 149.579 20.0739L148.076 18.7541ZM152.183 17.0338C151.829 17.4577 151.198 17.5143 150.775 17.1603C150.351 16.8063 150.294 16.1757 150.648 15.7518L152.183 17.0338ZM153.157 12.6659C153.5 12.2327 154.129 12.1593 154.562 12.502C154.995 12.8446 155.069 13.4735 154.726 13.9066L153.157 12.6659ZM157.169 10.7251C156.839 11.1682 156.213 11.2601 155.77 10.9305C155.327 10.6008 155.235 9.97436 155.564 9.53126L157.169 10.7251ZM6.88896 88.3256C8.23 88.1436 9.55601 87.9459 10.867 87.7329L11.1877 89.707C9.85942 89.9228 8.51612 90.1231 7.15782 90.3074L6.88896 88.3256ZM14.8658 87.0277C16.192 86.7751 17.5021 86.5068 18.7962 86.2233L19.2242 88.1769C17.9122 88.4644 16.5842 88.7364 15.24 88.9924L14.8658 87.0277ZM22.7548 85.2968C24.0641 84.9706 25.3561 84.6287 26.6308 84.2717L27.17 86.1977C25.8768 86.5598 24.5663 86.9066 23.2384 87.2375L22.7548 85.2968ZM30.5282 83.1177C31.8173 82.7149 33.0878 82.2965 34.3397 81.8635L34.9935 83.7536C33.7226 84.1932 32.433 84.6178 31.1247 85.0267L30.5282 83.1177ZM38.1535 80.4776C39.4183 79.9955 40.6628 79.4983 41.8873 78.9868L42.6582 80.8323C41.4143 81.3519 40.1502 81.8569 38.8659 82.3465L38.1535 80.4776ZM45.5943 77.3675C46.8295 76.8039 48.0427 76.2257 49.2341 75.6338L50.1239 77.425C48.9128 78.0266 47.6797 78.6143 46.4246 79.187L45.5943 77.3675ZM52.81 73.7823C54.0095 73.1353 55.1852 72.4746 56.337 71.8012L57.3465 73.5277C56.1748 74.2127 54.9792 74.8847 53.7594 75.5426L52.81 73.7823ZM59.7573 69.7211C60.9139 68.9897 62.0443 68.2457 63.1487 67.4903L64.2778 69.1411C63.1536 69.9101 62.0031 70.6673 60.8262 71.4115L59.7573 69.7211ZM66.4281 65.1585C67.5192 64.3522 68.5822 63.535 69.6172 62.7085L70.8652 64.2713C69.8107 65.1134 68.7279 65.9458 67.6168 66.7669L66.4281 65.1585ZM72.7291 60.124C73.7558 59.2371 74.7516 58.3415 75.7168 57.4388L77.0829 58.8996C76.0985 59.8202 75.0831 60.7334 74.0365 61.6375L72.7291 60.124ZM78.597 54.6336C79.5567 53.6596 80.4818 52.6796 81.3722 51.6957L82.8551 53.0376C81.9456 54.0427 81.0011 55.0433 80.0215 56.0374L78.597 54.6336ZM84.0039 48.6561C84.8694 47.6104 85.6962 46.5626 86.4846 45.5151L88.0826 46.7177C87.2756 47.7901 86.4296 48.8621 85.5447 49.9313L84.0039 48.6561ZM88.8266 42.2399C89.596 41.1052 90.3201 39.9739 90.9989 38.849L92.7113 39.8823C92.0139 41.0379 91.2708 42.199 90.4819 43.3624L88.8266 42.2399ZM92.9747 35.3549C93.6143 34.1427 94.1997 32.9423 94.7314 31.7577L96.556 32.5766C96.0065 33.8011 95.4023 35.0396 94.7436 36.2881L92.9747 35.3549ZM96.2373 28.0588C96.7089 26.7658 97.1122 25.499 97.4478 24.2639L99.3778 24.7884C99.0267 26.0806 98.6061 27.401 98.1163 28.7441L96.2373 28.0588ZM98.2979 20.3893C98.5216 19.0275 98.6527 17.72 98.6929 16.4756L100.692 16.5402C100.649 17.879 100.508 19.2735 100.271 20.7136L98.2979 20.3893ZM98.4989 12.6122C98.3048 11.2596 97.9764 10.0235 97.5203 8.91596L99.3697 8.15444C99.8962 9.43321 100.264 10.8322 100.479 12.3282L98.4989 12.6122ZM95.5808 5.76383C94.7723 4.85696 93.8049 4.11093 92.6739 3.54586L93.5677 1.75673C94.9257 2.4352 96.0965 3.33677 97.0737 4.43293L95.5808 5.76383ZM89.093 2.4561C87.9403 2.28754 86.6728 2.24699 85.2877 2.35179L85.1368 0.357491C86.6514 0.242888 88.0677 0.284904 89.3824 0.477152L89.093 2.4561ZM81.4342 2.96003C80.796 3.10818 80.1392 3.28205 79.4639 3.48261L78.8945 1.56537C79.6066 1.35389 80.3025 1.16956 80.982 1.01183L81.4342 2.96003ZM79.4639 3.48261C79.1299 3.5818 78.621 3.83576 77.9526 4.28873L76.8305 2.63318C77.5652 2.1352 78.2722 1.75018 78.8945 1.56537L79.4639 3.48261ZM74.9375 6.79553C74.0577 7.63764 73.127 8.61355 72.1843 9.70088L70.6731 8.39075C71.6539 7.25954 72.6272 6.23833 73.5547 5.35066L74.9375 6.79553ZM69.6785 12.8364C68.905 13.8865 68.1487 14.9972 67.4313 16.1554L65.7311 15.1022C66.4782 13.8961 67.2647 12.7411 68.0682 11.6503L69.6785 12.8364ZM65.4757 19.6433C64.8786 20.8325 64.338 22.0518 63.8726 23.2893L62.0006 22.5852C62.4931 21.2757 63.0627 19.9919 63.6883 18.7459L65.4757 19.6433ZM62.7135 27.0733C62.4114 28.3656 62.2082 29.6619 62.1225 30.9504L60.1269 30.8176C60.2206 29.4093 60.4418 28.0049 60.7661 26.618L62.7135 27.0733ZM62.2422 34.8288C62.4134 36.0935 62.7216 37.3404 63.186 38.5591L61.3171 39.2713C60.7954 37.9022 60.451 36.5057 60.2603 35.0972L62.2422 34.8288ZM64.9631 41.9678C65.6501 42.9845 66.476 43.9773 67.4574 44.9374L66.0587 46.367C64.9852 45.3167 64.0717 44.2208 63.306 43.0876L64.9631 41.9678ZM70.485 47.4354C71.5 48.1481 72.6236 48.8353 73.8644 49.492L72.9288 51.2597C71.6196 50.5668 70.4239 49.8364 69.3357 49.0722L70.485 47.4354ZM77.4669 51.1717C78.0795 51.4235 78.7141 51.669 79.3711 51.9079L78.6877 53.7875C78.0057 53.5396 77.3456 53.2842 76.7067 53.0216L77.4669 51.1717ZM79.3711 51.9079C79.9827 52.1303 80.5966 52.3321 81.2126 52.514L80.6461 54.4321C79.9909 54.2386 79.338 54.024 78.6877 53.7875L79.3711 51.9079ZM84.975 53.3785C86.2503 53.5908 87.5335 53.7251 88.8227 53.7862L88.7281 55.7839C87.363 55.7193 86.0017 55.5769 84.6466 55.3514L84.975 53.3785ZM92.7007 53.7549C93.9849 53.6755 95.2734 53.5293 96.5643 53.3207L96.8834 55.2951C95.5309 55.5136 94.1771 55.6675 92.8242 55.7511L92.7007 53.7549ZM100.382 52.5277C101.634 52.2113 102.886 51.8408 104.137 51.4203L104.774 53.316C103.478 53.7519 102.176 54.1371 100.872 54.4668L100.382 52.5277ZM107.807 50.044C109.005 49.5488 110.2 49.0113 111.39 48.4354L112.262 50.2355C111.038 50.8277 109.807 51.3814 108.57 51.8925L107.807 50.044ZM114.888 46.6255C116.032 45.9957 117.17 45.3334 118.298 44.6421L119.343 46.3476C118.189 47.0543 117.025 47.7322 115.852 48.3777L114.888 46.6255ZM121.619 42.5107C122.709 41.7792 123.789 41.0233 124.858 40.2467L126.034 41.8645C124.946 42.6552 123.845 43.4254 122.733 44.1715L121.619 42.5107ZM128.017 37.8659C129.058 37.0538 130.085 36.2246 131.098 35.3818L132.377 36.9191C131.349 37.7748 130.305 38.6172 129.247 39.4426L128.017 37.8659ZM134.105 32.8037C135.101 31.9249 136.08 31.0356 137.041 30.1397L138.405 31.6024C137.431 32.5104 136.438 33.4119 135.429 34.3032L134.105 32.8037ZM139.904 27.3986C140.856 26.4636 141.787 25.5254 142.695 24.5881L144.132 25.9796C143.213 26.9285 142.27 27.8785 141.306 28.8255L139.904 27.3986ZM145.421 21.7037C146.335 20.7131 147.221 19.7283 148.076 18.7541L149.579 20.0739C148.713 21.0601 147.816 22.057 146.891 23.0597L145.421 21.7037ZM150.648 15.7518C151.525 14.7015 152.363 13.6706 153.157 12.6659L154.726 13.9066C153.92 14.9254 153.072 15.97 152.183 17.0338L150.648 15.7518ZM155.564 9.53126C155.967 8.98986 156.355 8.45955 156.727 7.9416L158.351 9.1082C157.972 9.63554 157.578 10.1749 157.169 10.7251L155.564 9.53126Z" />
              </SvgArrow>
            </ButtonInner>
          )}
        </TitleWrapper>
        <RelativeWrapper>
          <div>
            {bookMockup ? (
              <HoverableDropzone
                isSalePage
                css={imageStyles as never}
                disabled={!editable}
                resizeSelector={editable}
                handleResize={({ width, height }) => {
                  setResize({ width, height });
                  updateImageMeta(meta['accessorKey'], width, 'width');
                  updateImageMeta(meta['accessorKey'], height, 'height');
                }}
                allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
                onUpload={handleUpload}
                loading={isLoading}
                alreadyUploaded={Boolean(bookMockup)}
                imageFile={bookMockup}
                initialPosition={{
                  x: meta['x_1'] || 0,
                  y: meta['y_1'] || 0,
                  width: meta['d_width_1'],
                  height: meta['d_height_1']
                }}
                meta={meta}
                positionEnabled
                handlePosition={(width: number, height: number, x: number, y: number) => {
                  updateImageMeta(meta['accessorKey'], x, 'x_1');
                  updateImageMeta(meta['accessorKey'], y, 'y_1');
                  updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                  updateImageMeta(meta['accessorKey'], height, 'd_height_1');
                }}
                imageAspectRatio={{
                  ratio: '3x4',
                  pixels: '600x800'
                }}>
                <BookMediaImg
                  isImageUploaded={isImageUploaded}
                  src={bookMockup}
                  alt="Book Mockup"
                  width={resize.width || 500}
                  height={resize.height || 500}
                />
              </HoverableDropzone>
            ) : (
              <StyledMockup
                mockup={mockup?.file}
                mockup_id={mockup?.id}
                cover_thumbnail={coverThumbnail}
              />
            )}
          </div>

          <SvgBg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 703 703">
            <circle className="cls-1" cx="351.5" cy="351.5" r="350" />
            <circle className="cls-3" cx="351.5" cy="351.5" r="306.34" />
            <circle fill={props.button?.color || COLOR_ORANGE} cx="46.5" cy="344.5" r="15.5" />
            <circle fill={props.button?.color || COLOR_ORANGE} cx="673.5" cy="203.5" r="15.5" />
            <ellipse
              fill={props.button?.color || COLOR_ORANGE}
              cx="625"
              cy="483.5"
              rx="15"
              ry="15.5"
            />
          </SvgBg>
        </RelativeWrapper>
      </StyledWrapper>
      <BenefitWrapper>
        <Wrapper>
          <Benefits>
            {benefits.map(({ content, icon, title }, index) => {
              const isTitleBenEmpty = checkTitleBenefits(index);
              const isContentBenEmpty = checkContentBenefits(index);
              return (
                <BenefitItem
                  // style={{
                  //   display: isTitleBenEmpty && isContentBenEmpty && !editable ? 'flex' : 'none'
                  // }}
                  // TODO: Replace with dynamic values later
                  key={`${icon?.toString()}-${index}`}>
                  <StyledSvgIcons
                    iconName={`icon_${index + 1}` as 'icon_1' | 'icon_2' | 'icon_3'}
                    backgroundColor={secondaryColor}
                  />
                  <BenefitTxtInner>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      onClick={updateActiveElement}
                      onBlur={resetActiveElement}
                      data-accessor-parent={meta['accessorKey']}
                      data-accessorkey="title"
                      data-benefits={true}
                      data-placeholder="+"
                      editable={process.env['APP_TYPE'] === 'dashboard'}
                      isEmpty={isTitleBenEmpty}
                    />
                    <TextBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      onClick={updateActiveElement}
                      onBlur={resetActiveElement}
                      data-accessor-parent={meta['accessorKey']}
                      data-accessorkey="content"
                      data-benefits={true}
                      data-placeholder="+"
                      editable={process.env['APP_TYPE'] === 'dashboard'}
                      isEmpty={isContentBenEmpty}
                    />
                  </BenefitTxtInner>
                </BenefitItem>
              );
            })}
          </Benefits>
        </Wrapper>
      </BenefitWrapper>
      <WhiteInner>
        <RelativeWhite>
          <div className="hero-white-v5 hero-white-v5_one"></div>
          <div className="hero-white-v5 hero-white-v5_two"></div>
          <div className="hero-white-v5 hero-white-v5_three"></div>
          <div className="hero-white-v5 hero-white-v5_four"></div>
          <div className="hero-white-v5 hero-white-v5_five"></div>
          <div className="hero-white-v5 hero-white-v5_six"></div>
        </RelativeWhite>
      </WhiteInner>
      <ColoredBg style={{ background: secondaryColor }} />
    </HeroDetailed>
  );
};
